<script setup>
import { onMounted, onBeforeUnmount, ref } from "vue";
import { apiFetch } from "../lib/api";
import { getCachedCurrentUser } from "../lib/current-user";
import UiDialog from "./ui/UiDialog.vue";

const open = ref(false);
const pending = ref(false);
const error = ref("");
const needsSignIn = ref(false);
let expectedEmail;

function show() {
  if (open.value) return;
  expectedEmail = getCachedCurrentUser()?.email;
  error.value = "";
  needsSignIn.value = false;
  open.value = true;
}

async function renew() {
  if (pending.value) return;
  pending.value = true;
  error.value = "";
  try {
    const response = await apiFetch("/api/auth/refresh", { method: "POST" });
    if (!response.ok) {
      needsSignIn.value = true;
      error.value =
        "Please sign in in a separate tab, then return here and continue. Keep this page open to keep your unsaved work.";
      return;
    }
    const me = await apiFetch("/api/auth/me");
    if (!me.ok) throw new Error("Session verification failed");
    const user = await me.json();
    if (
      !user.authenticated ||
      (expectedEmail && user.email !== expectedEmail)
    ) {
      needsSignIn.value = true;
      error.value =
        "Please sign back in with the same account you were using on this page.";
      return;
    }
    open.value = false;
  } catch {
    error.value =
      "Couldn't reconnect. Check your connection and try again. Your work is still on this page.";
  } finally {
    pending.value = false;
  }
}

onMounted(() => window.addEventListener("pp-session-expired", show));
onBeforeUnmount(() => window.removeEventListener("pp-session-expired", show));
</script>

<template>
  <UiDialog
    :model-value="open"
    aria-label="Stay signed in"
    :close-on-backdrop="false"
    :close-on-escape="false"
    :z-index="33000"
    panel-class="w-full max-w-md rounded-xl bg-[var(--color-surface)] p-5 text-[var(--color-text)] shadow-xl"
  >
    <h2 class="text-lg font-semibold">Stay signed in?</h2>
    <p class="mt-3 text-sm">
      Your session needs to be renewed. Your current page and unsaved work will
      stay open. After reconnecting, retry your last action.
    </p>
    <p v-if="error" class="mt-3 text-sm text-amber-400" role="alert">
      {{ error }}
    </p>
    <a
      v-if="needsSignIn"
      href="/login"
      target="_blank"
      rel="noopener"
      class="mt-4 flex min-h-11 items-center justify-center rounded-lg border border-current px-4"
      >Sign in in a new tab</a
    >
    <button
      type="button"
      :disabled="pending"
      :aria-busy="pending"
      class="mt-4 min-h-11 w-full rounded-lg bg-[var(--color-primary)] px-4 py-3 text-white"
      @click="renew"
    >
      {{
        pending
          ? "Reconnecting…"
          : needsSignIn
            ? "I've signed in — continue"
            : "Stay signed in"
      }}
    </button>
    <button
      type="button"
      :disabled="pending"
      class="mt-2 min-h-11 w-full rounded-lg px-4 py-3 text-sm"
      @click="open = false"
    >
      Keep working on this page
    </button>
  </UiDialog>
</template>
